"use client";

import { X } from "lucide-react";
import { useState } from "react";
import { usePathname } from "next/navigation";
import { useSession } from "next-auth/react";
import Link from "next/link";

import { promotions } from "@/config/promotions";

export function BlackFridayBanner() {
  if (!promotions.blackFridayEnabled) {
    return null;
  }

  return <BlackFridayBannerInner />;
}

function BlackFridayBannerInner() {
  const [isVisible, setIsVisible] = useState(true);
  const pathname = usePathname();
  const { data: session } = useSession();
  const isLandingPage = pathname === "/";
  const hasPaidAccess = session?.user?.hasPaidAccess ?? false;

  // Show on landing page OR when logged in without paid access
  const shouldShow =
    (isLandingPage || (!hasPaidAccess && session)) && isVisible;

  if (!shouldShow) {
    return null;
  }

  const redirectToCheckout = () => {
    if (isLandingPage) {
      // Same behavior as "Get Lifetime Access" button - redirect to signin with callback
      const callbackUrl = `/checkout?plan=lifetime`;
      const encodedCallbackUrl = encodeURIComponent(callbackUrl);
      window.location.href = `/signin?callbackUrl=${encodedCallbackUrl}`;
    }
  };

  const bannerContent = (
    <div className="mx-auto max-w-7xl px-6">
      <div className="flex items-center justify-center gap-4 py-3">
        <span className="text-xs font-semibold text-amber-600 dark:text-amber-500 uppercase tracking-wider">
          Black Friday Deal:
        </span>
        <div className="flex items-baseline gap-2">
          <span className="text-xs text-amber-600/70 dark:text-amber-500/70 line-through">
            $35
          </span>
          <span className="text-base font-bold text-amber-600 dark:text-amber-500">
            $25
          </span>
          <span className="text-xs font-medium text-amber-600 dark:text-amber-500">
            Lifetime
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <div
      data-black-friday-banner-root
      className="sticky top-0 left-0 right-0 z-50 w-full border-b border-amber-500/20 bg-amber-500/5 backdrop-blur-sm"
    >
      {isLandingPage ? (
        <button
          onClick={redirectToCheckout}
          className="w-full hover:bg-amber-500/10 transition-colors duration-200 cursor-pointer"
        >
          {bannerContent}
        </button>
      ) : (
        <Link
          href="/checkout?plan=lifetime"
          className="block w-full hover:bg-amber-500/10 transition-colors duration-200"
        >
          {bannerContent}
        </Link>
      )}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsVisible(false);
        }}
        className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-muted/50"
        aria-label="Close banner"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
